import axios from 'axios';
import fileDownload from 'js-file-download';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import {
	TAvailableReportsResponse,
	TAvailableReportsSearchRequest,
	TDownloadAvailableReportRequest,
	TDownloadedReportFile,
	TReportType,
} from './types';
import {
	downloadAvailableReportFinished,
	downloadAvailableReportRequest,
	getReportTypesFailure,
	getReportTypesRequest,
	getReportTypesSuccess,
	searchAvailableReportsFailure,
	searchAvailableReportsRequest,
	searchAvailableReportsSuccess,
} from './reducer';

function* getReportTypesWorker() {
	try {
		const response: TReportType[] = yield call(api.reports.getReportTypes);
		yield put(getReportTypesSuccess(response));
	} catch (error) {
		yield put(getReportTypesFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error.response?.data?.message ?? error.message ?? 'Failed to load report types'),
				'error',
			);
		}
	}
}

function* searchAvailableReportsWorker({ payload }: PayloadAction<TAvailableReportsSearchRequest>) {
	try {
		const response: TAvailableReportsResponse = yield call(
			api.reports.searchAvailableReports,
			payload,
		);
		yield put(searchAvailableReportsSuccess(response));
	} catch (error) {
		yield put(searchAvailableReportsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error.response?.data?.message ?? error.message ?? 'Failed to load reports'),
				'error',
			);
		}
	}
}

function* downloadAvailableReportWorker({
	payload,
}: PayloadAction<TDownloadAvailableReportRequest>) {
	try {
		const file: TDownloadedReportFile = yield call(api.reports.downloadAvailableReport, payload);
		if (file.blob) fileDownload(file.blob, file.fileName);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			// The request expects a blob, so an error body arrives as a Blob holding the JSON response.
			const data = error.response?.data;
			let message: string | undefined =
				typeof data?.message === 'string' ? data.message : undefined;
			if (message === undefined && data instanceof Blob) {
				try {
					const text: string = yield call([data, 'text']);
					const parsed = text ? JSON.parse(text) : {};
					message = parsed?.message ?? parsed?.error ?? (text || 'Download failed');
				} catch {
					message = 'Download failed';
				}
			}
			notificationContainer(String(message ?? error.message ?? 'Download failed'), 'error');
		}
	} finally {
		yield put(downloadAvailableReportFinished(payload.id));
	}
}

export function* reportsSaga() {
	yield takeLatest(getReportTypesRequest.type, getReportTypesWorker);
	yield takeLatest(searchAvailableReportsRequest.type, searchAvailableReportsWorker);
	yield takeEvery(downloadAvailableReportRequest.type, downloadAvailableReportWorker);
}
